import {
    useRef,
    useState
} from 'react';

import {Badge, SimpleGrid, Text} from '@mantine/core';

import {useProducts} from '../hooks/useProducts.ts';
import {useProductsSelection} from '../hooks/useProductsSelection.ts';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {update} from '../services/ProductsService.ts';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableColumn} from "mantine-datatable";
import {DataTableTokenPagination} from "../../../components/DataTableTokenPagination.tsx";
import {Product, ProductStatus} from '../types';
import {useQueryClient} from '@tanstack/react-query';
import {formatTimestamp} from "../../../utils/TableTimestamps.tsx";
import {I18n} from "@aws-amplify/core";

interface RecordsTableProps {
    statusFilter?: ProductStatus | null;
}

export const RecordsTable = ({statusFilter}: RecordsTableProps) => {
    const {selectedRecords, setSelectedRecords} = useProductsSelection();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Product | null>(null);
    const queryClient = useQueryClient();

    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useProducts(statusFilter);

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const handleRecordClick = (record: Product) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: Product) => {
        if (selectedRecord?.id) {
            const {
                lineItems: _lineItems,
                created: _created,
                updated: _updated,
                createdByUsername: _createdByUsername,
                createdByUser: _createdByUser,
                ...rest
            } = values;
            await update(selectedRecord.id, rest as Product);
            queryClient.invalidateQueries({queryKey: ['products']});
            queryClient.invalidateQueries({queryKey: ['all-products']});
            queryClient.invalidateQueries({queryKey: ['product', selectedRecord.id]});
        }
    };

    const columns: DataTableColumn<Product>[] = [
        {
            accessor: 'name',
            title: I18n.get('Name'),
            width: '1fr',
            render: (record: Product) => (
                <Text
                    c="blue"
                    style={{cursor: 'pointer'}}
                    onClick={() => handleRecordClick(record)}
                >
                    {record.name}
                </Text>
            ),
        },
        {
            accessor: 'status',
            title: I18n.get('Status'),
            width: '120px',
            render: (item: Product) => (
                <Badge
                    color={item.status === 'Active' ? 'green' : item.status === 'Draft' ? 'yellow' : 'red'}
                    variant="light"
                >
                    {item.status || 'UNKNOWN'}
                </Badge>
            ),
        },
        {
            accessor: 'created',
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: Product) => formatTimestamp(item.created as string),
        },
    ];

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken}
                                      handleLoadMore={fetchRecords}/>

            <FormDrawer
                record={selectedRecord || {} as Product}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};
